import "./list.css"

import React from "react"
import { styled } from "@mui/material/styles"

import { GatsbyImage } from "gatsby-plugin-image"
import { Link } from "gatsby"
import { getEntityUrl, getAccessoryIcon } from "../../util"

import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import {EntityListItems} from "../../components/List";
import {findListItemRole} from "../../repository/EntityRepository";

const PREFIX = "List"

const classes = {
  listItem: `${PREFIX}-listItem`,
  listLink: `${PREFIX}-listLink`,
  listImg: `${PREFIX}-listImg`,
  titleDiv: `${PREFIX}-titleDiv`,
  titleFlex: `${PREFIX}-titleFlex`,
  title1: `${PREFIX}-title1`,
  title2: `${PREFIX}-title2`,
  title3: `${PREFIX}-title3`,
  numCode: `${PREFIX}-numCode`,
  playButton: `${PREFIX}-playButton`,
  associatedEntity: `${PREFIX}-associatedEntity`,
  associatedLinkIcon: `${PREFIX}-associatedLinkIcon`,
  associatedLinkText: `${PREFIX}-associatedLinkIcon`,
}

const Root = styled("ul")(({ theme }) => ({
  listStyleType: "none",
  margin: "0",
  marginTop:"0px",
  padding: 0,


  [`& .${classes.listItem}`]: {
    textDecoration: "none",
    color: theme.palette.primary.main,
    background: theme.palette.mode === "light" ? "#ffffff" : "#333",
    borderTop: '1px solid',
    borderColor: theme.palette.mode === "light" ? "#116fcf" : "#737373",
    listStyle: "none",
    display: "flex",
    flexDirection:"row",
    justifyContent: "flex-start",
    minHeight: "48px",
    margin: "0 8px",
    padding: '10px 0',
    overflow: "hidden",
    alignItems: "center",
  },

  [`& .${classes.listLink}`]: {
    textDecoration: "none"
  },

  [`& .${classes.listImg}`]: {
    width: 100,
    minWidth: 100,
    height: 90,
    margin: '0',
    marginRight: 0,
  },

  [`& .${classes.titleDiv}`]: {
    width: "100%",
    overflow: "hidden",
    padding: "0px 20px 0px 11px"
  },

  [`& .${classes.titleFlex}`]: {
    display: "flex",
    flexGrow: "1",
    alignItems: "center",
    position: "relative",
    minHeight: "60px",
  },

  [`& .${classes.title1}`]: {
    fontSize: '15px',
    textTransform: 'none',
    marginBottom: 0,
    lineHeight: 1.15,
    fontWeight: 700,
  },

  [`& .${classes.title2}`]: {
    fontSize: '15px',
    lineHeight: 1.15,
    marginTop: 2,
    paddingRight: 20,
  },

  [`& .${classes.title3}`]: {
    fontSize: "0.9rem",
    marginBottom: "0px",
    marginTop: "2px",
    fontWeight: "normal"
  },

  [`& .${classes.numCode}`]: {
    position: "absolute",
    right: "10px",
    bottom: "7px",
    fontSize: "13px",
    fontWeight: "bold",
    display:"none"
  },

  [`& .${classes.playButton}`]: {
    position: "absolute",
    right: "0px",
    top: "50%",
    transform: "translateY(-50%)"
  },

  [`& .${classes.associatedEntity}`]: {
    display: "block",
    position: "absolute",
    right: "6px",
    bottom: "6px",
    fontSize: "14px",
  },

  [`& .${classes.associatedEntity} a`]: {
    textDecoration: "none",
    color: theme.palette.primary.main,
  },

  [`& .${classes.associatedLinkIcon}`]: {
  },

  [`& .${classes.associatedLinkText}`]: {
  },
}))

const WmaListItemLayout = ({ entity, title1, title2, title3, numCode, pageContext, sublistRole, image, associatedEntity, oaiRole, config }) => {
  const { defaultLocale } = pageContext;
  const AccessoryIcon = getAccessoryIcon({ defaultIcon: ArrowForwardIosIcon, role: findListItemRole(entity)});
  const listType = config?.listType || 'standard'
  const displayOnlyTitles = listType === 'titleOnly'

  return (
      <div style={{ position: 'relative' }} className={"list-item" + (sublistRole ? " inlinelist-item" : "")}>
        <Link to={getEntityUrl({ entity, defaultLocale })} className={classes.listLink} >
          <li className={classes.listItem}>
            {image && !displayOnlyTitles && (
                <GatsbyImage
                    className={classes.listImg}
                    image={image}
                    alt={title1}
                    layout="fullWidth"
                />
            )}
            <div className={classes.titleFlex}>
             { sublistRole && sublistRole.backgroundColor && !displayOnlyTitles && <div className="inlinelist-marker" style={{ backgroundColor: sublistRole.backgroundColor }}> </div>}
              <div className={classes.titleDiv + " list-item-title"}>
                <div className={classes.title1 + " searchable"}>{title1}</div>
                <div className={classes.title2 + " searchable"}>{title2}</div>
                <div className={classes.title3 + " searchable"}>{title3}</div>
                <div className={classes.numCode}>{numCode}</div>
                {!displayOnlyTitles && <div className={classes.playButton}>
                  <AccessoryIcon color="secondary" fontSize="small" />
                </div>}
              </div>
            </div>
          </li>
        </Link>
        { associatedEntity && !displayOnlyTitles && associatedEntity.entity &&
            <div className={classes.associatedEntity}>
              <Link to={getEntityUrl({ entity: associatedEntity.entity, defaultLocale })} {...(associatedEntity.title && { title: associatedEntity.title })} >
                <span className={classes.associatedLinkText}>{associatedEntity.text && associatedEntity.text}</span>
                <span className={classes.associatedLinkIcon} role={"img"} aria-label={"information icon"}>&nbsp;ℹ️</span>
              </Link>
            </div>
        }
      </div>
  )
}

export const WmaInlineListHeadlineLayout = ({ entity, listRole }) => {
  const title1 = listRole?.line1 || entity.title || '';
  const title2 = listRole?.line2 || false;

  const sublistStyles = {
    ...(listRole.backgroundColor ? {backgroundColor: listRole.backgroundColor} : {}),
    ...(listRole.textColor ? {color: listRole.textColor} : {})
  }

  return (
      <div className={"list-item inlinelist-item inlinelist-headline"}>
        <li className={classes.listItem} style={sublistStyles}>
          <div className={classes.titleFlex}>
            <div className={classes.titleDiv}>
              <div className={classes.title1}>{title1}</div>
              <div className={classes.title2}>{title2}</div>
            </div>
          </div>
        </li>
      </div>
  )
}

const List = ({ entity, pageContext, config }) => {
  const listType = config?.listType || 'standard'

  return (
      <Root className={"list-type-" + listType}>
          <EntityListItems entity={entity} pageContext={pageContext} config={config} listItemLayout={WmaListItemLayout} inlineListHeadlineLayout={WmaInlineListHeadlineLayout} />
      </Root>
  );
};

export default List
